/**
 * CR-7714: call-off load totals that say when they are incomplete.
 *
 * A line with no pallet quantity (or no unit weight) used to add 0 to the
 * total, so the printed sheet and the Total Pallets field read as complete
 * while lines were missing from them, and the lorry could be under-booked.
 * The totals now count those lines separately so the screen can say so.
 */

interface LoadLine {
  requested_qty: string;
  pallet_qty?: string | null;
  unit_weight_kg?: string | null;
}

/** Whole pallets for one line, or null when the line has no pallet quantity. */
export function palletsOf(line: LoadLine): number | null {
  const qty = parseFloat(line.requested_qty) || 0;
  const perPallet = parseFloat(line.pallet_qty || '');
  return perPallet > 0 ? Math.ceil(qty / perPallet) : null;
}

/** Weight in kg for one line, or null when the line has no unit weight. */
export function weightOf(line: LoadLine): number | null {
  const qty = parseFloat(line.requested_qty) || 0;
  const unit = parseFloat(line.unit_weight_kg || '');
  return unit > 0 ? qty * unit : null;
}

export interface LoadTotal {
  /** Sum over the lines that have the figure. */
  total: number;
  /** Lines that have the figure. */
  known: number;
  /** Lines left out because they have no figure. */
  missing: number;
}

function sumKnown(values: Array<number | null>): LoadTotal {
  return values.reduce<LoadTotal>(
    (acc, v) => (v === null
      ? { ...acc, missing: acc.missing + 1 }
      : { ...acc, known: acc.known + 1, total: acc.total + v }),
    { total: 0, known: 0, missing: 0 },
  );
}

export const palletTotal = (lines: LoadLine[]): LoadTotal => sumKnown(lines.map(palletsOf));
export const weightTotal = (lines: LoadLine[]): LoadTotal => sumKnown(lines.map(weightOf));

/** "12", "12 (3 lines without pallet data)", or "— (3 lines without pallet data)"
 *  when no line has the figure at all (never a 0 that looks like a total). */
export function describeTotal(t: LoadTotal, what: string, format: (n: number) => string = String): string {
  const base = t.known === 0 && t.missing > 0 ? '—' : format(t.total);
  if (t.missing === 0) return base;
  return `${base} (${t.missing} ${t.missing === 1 ? 'line' : 'lines'} without ${what})`;
}
